// The SCORE surface's pager, shared by the workbench score cell
// (NotationPane) and Score Studio — one place that knows how an engraved
// strip becomes pages, so the reading view and the editor cannot drift on
// paper size, page breaks or measurement discipline.
//
// The division of labour matches the chart pager: `sheetPages.ts` decides
// (pure, tested), this hook MEASURES. Its caller renders one windowed
// ScoreView per page and reports the engraving's per-system slice heights
// back through `onSystemSpans`; `packSpans` deals those into pages
// sequentially. Spans are pagination-INVARIANT (they come from the
// engraving, not from the windows), which is why the measure→plan loop has a
// fixed point.
//
// Every measurement passes the MEASURE GATE before becoming state: an
// unchanged one schedules nothing, and a genuinely alternating one (layout
// feedback) is held — see the gate's own doc. Without that, React's
// nested-update limit unmounts the tree, which is what a blank screen was.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '@/state/store';
import { measureSheetBand, MIN_SHEET_PAGE_HEIGHT } from './sheetBand';
import {
  admitMeasure,
  createMeasureGate,
  MEASURE_SETTLE_MS,
  measureSignature,
  needsPaperPages,
  packSpans,
  sheetPageWidth,
} from './sheetPages';

/** Notation reflows gracefully at any width (systems justify, the 4-bar cap
 * is soft), so the score's minimum page needs no chart-style legibility
 * elevation — but it stays above useNotationMetrics' 700px mobile-margin
 * threshold, or a desktop page would flip to the phone margins. */
export const SCORE_MIN_PAGE_WIDTH = 720;
/** Below the engraving: the page-number lane plus the card's own padding. */
const SCORE_PAGE_BOTTOM_CHROME = 28;
/** A4 portrait (297/210) — the shape a page falls back to; see below. */
const PAPER_ASPECT = 1.294;

export interface ScorePager {
  /** True only when pages are wanted AND the band can hold a legible page —
   * so a narrow window falls back to the flowing strip on its own, and pages
   * again once it widens. */
  paged: boolean;
  /** The pager root (measures the band). */
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  /** An element rendered whether or not the pages are, whose content box the
   * pager root fills when it is there (its parent, in practice). While the
   * strip flows the root is gone, and this is how the pager still sees the
   * window widen enough to page again. */
  probeRef: React.MutableRefObject<HTMLDivElement | null>;
  /** The page-cards grid (its document top anchors the band). */
  gridRef: React.MutableRefObject<HTMLDivElement | null>;
  /** Hand this to page 1's ScoreView only — one report per engraving. */
  onSystemSpans: (spans: number[]) => void;
  /** How many systems the engraving has, reported whether or not pages are
   * on — the printed sheet draws one block per system. */
  systemCount: number;
  columns: number;
  pageWidth: number;
  minHeight: number;
  windows: readonly (readonly [number, number])[];
}

export function useScorePages(active: boolean): ScorePager {
  const focusMode = useStore((s) => s.focusMode);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const probeRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [spans, setSpans] = useState<number[]>([]);
  const spansRef = useRef<number[]>([]);
  const gateRef = useRef(createMeasureGate());
  const retryRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [band, setBand] = useState(() => ({
    availWidth: typeof window !== 'undefined' ? Math.max(320, window.innerWidth - 48) : 1200,
    availHeight:
      typeof window !== 'undefined'
        ? Math.max(MIN_SHEET_PAGE_HEIGHT, window.innerHeight - 260)
        : 640,
    // Page 1's content inset: card padding PLUS the masthead printed on it.
    topChrome: 20,
    // What that masthead occupies, so pages 2+ hand the band back to music.
    headerBlock: 0,
  }));
  // The last admitted band, for a measurement that can read only part of it.
  const bandRef = useRef(band);

  const onSystemSpans = useCallback((next: number[]) => {
    const prev = spansRef.current;
    if (prev.length === next.length && prev.every((v, i) => Math.abs(v - next[i]) < 1)) return;
    spansRef.current = next;
    setSpans(next);
  }, []);

  const measure = useCallback(() => {
    const el = containerRef.current;
    const pagesEl = gridRef.current;
    let next: typeof bandRef.current;
    if (el && pagesEl) {
      const nextBand = measureSheetBand(el, pagesEl);
      const surface = el.querySelector<HTMLElement>('.sheet-page .score-page-surface');
      const page = surface?.closest('.sheet-page');
      let topChrome = 20;
      let headerBlock = 0;
      if (surface && page) {
        const surfaceTop = surface.getBoundingClientRect().top;
        topChrome = Math.max(0, surfaceTop - page.getBoundingClientRect().top);
        const headerEl = page.querySelector<HTMLElement>('.sheet-page-header');
        if (headerEl) headerBlock = Math.max(0, surfaceTop - headerEl.getBoundingClientRect().top);
      }
      next = { ...nextBand, topChrome, headerBlock };
    } else {
      // Flowing: the pager root is not rendered. Measuring only through it
      // meant a strip never saw the window widen again and stayed a strip
      // until a reload. Only the WIDTH decides whether to page, so read it off
      // the probe's content box, which the root fills once it mounts: as wide
      // or wider, since the spread releases the column cap, so the switch does
      // not flip straight back (a classic scrollbar's reserved gutter, the one
      // way the root comes out narrower, is a bounce the gate holds). Height
      // and chrome keep their last reading; the root re-measures them all.
      const probe = probeRef.current;
      if (!probe) return;
      const style = window.getComputedStyle(probe);
      const padding = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
      next = { ...bandRef.current, availWidth: probe.clientWidth - padding };
    }
    const signature = measureSignature([
      next.availWidth,
      next.availHeight,
      next.topChrome,
      next.headerBlock,
    ]);
    const verdict = admitMeasure(gateRef.current, signature, performance.now());
    // A refusal is not the end of the story. `busy` means the burst was full —
    // ordinary during a drag, whose LAST event lands in a full burst — so
    // re-measure once the burst expires, or the layout stays frozen at a
    // mid-drag size (pagination stuck, or gone when the held width sits under
    // the page minimum). `cycling` is deliberately NOT retried: that hold is
    // the point, and retrying would turn it into a slow flip-flop.
    if (verdict !== 'admitted') {
      if (verdict === 'busy' && retryRef.current === null) {
        retryRef.current = setTimeout(() => {
          retryRef.current = null;
          measureRef.current();
        }, MEASURE_SETTLE_MS + 60);
      }
      return;
    }
    bandRef.current = next;
    setBand(next);
  }, []);
  const measureRef = useRef(measure);
  measureRef.current = measure;
  useEffect(
    () => () => {
      if (retryRef.current !== null) clearTimeout(retryRef.current);
    },
    [],
  );

  const paged = active && band.availWidth >= SCORE_MIN_PAGE_WIDTH;
  // Measure after every commit while pages are wanted (flowing too, off the
  // probe); the gate keeps it convergent.
  useLayoutEffect(() => {
    if (active) measure();
  });
  useEffect(() => {
    if (!active) return;
    // Whichever of the two is rendered: `paged` re-runs this as they swap.
    const el = containerRef.current ?? probeRef.current;
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (el && observer) observer.observe(el);
    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [active, paged, measure]);
  // Focus mode animates the chrome away; measure again once it has settled.
  useEffect(() => {
    if (!active) return;
    const timer = setTimeout(measure, 350);
    return () => clearTimeout(timer);
  }, [active, focusMode, measure]);

  const { fit, pageWidth } = sheetPageWidth(band.availWidth, SCORE_MIN_PAGE_WIDTH);
  // Band-fit or paper, from the content alone (see needsPaperPages).
  const bandContent = Math.max(0, band.availHeight - band.topChrome - SCORE_PAGE_BOTTOM_CHROME);
  const pageHeight = needsPaperPages(spans, bandContent)
    ? Math.max(band.availHeight, Math.round(pageWidth * PAPER_ASPECT))
    : band.availHeight;
  // Page 1 carries the masthead; every later page gets that band back.
  const firstPageHeight = Math.max(0, pageHeight - band.topChrome - SCORE_PAGE_BOTTOM_CHROME);
  const laterPageHeight = Math.max(
    0,
    pageHeight - (band.topChrome - band.headerBlock) - SCORE_PAGE_BOTTOM_CHROME,
  );
  const windows = useMemo<readonly (readonly [number, number])[]>(() => {
    if (!spans.length) return [[0, Number.MAX_SAFE_INTEGER]];
    const packed = packSpans(spans, (page) => (page === 0 ? firstPageHeight : laterPageHeight));
    // The LAST page is open-ended, and that is load-bearing: the span report
    // arrives a render AFTER the engraving changes, so an edit that adds a
    // system (or a wider page that re-breaks the music) would otherwise leave
    // that system belonging to no page at all — it simply vanished until the
    // next report. ScoreView clamps the window to the systems it has, so an
    // open end means the pages always cover the whole score; the next report
    // tightens the pagination. Pinned by a Studio note-entry e2e.
    return packed.map((win, i) =>
      i === packed.length - 1 ? ([win[0], Number.MAX_SAFE_INTEGER] as const) : win,
    );
  }, [spans, firstPageHeight, laterPageHeight]);

  return {
    paged,
    containerRef,
    probeRef,
    gridRef,
    onSystemSpans,
    systemCount: spans.length,
    columns: Math.min(fit, windows.length),
    pageWidth: Math.floor(pageWidth),
    minHeight: Math.round(pageHeight),
    windows,
  };
}
